import React from "react";
import { Link } from "react-router-dom";
import { Car, Bike, Zap, Home as HomeIcon, HeartPulse, Activity, Briefcase, Plane, PawPrint, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSeoMeta, Breadcrumb } from "../lib/landingShared";

const SOLUZIONI = [
  { key: "auto",         icon: Car,        title: "Assicurazione Auto",        landing: "/assicurazione-auto-milano", desc: "RCA, furto, incendio, kasko e assistenza. Analizziamo il tuo profilo per la copertura giusta." },
  { key: "moto",         icon: Bike,       title: "Assicurazione Moto",         landing: null,                          desc: "RCA moto e scooter con garanzie accessorie: furto, incendio, assistenza stradale, infortuni conducente." },
  { key: "casa",         icon: HomeIcon,   title: "Assicurazione Casa",         landing: "/assicurazione-casa",         desc: "Protezione dell'abitazione: incendio, furto, danni da acqua, responsabilità civile, eventi atmosferici." },
  { key: "vita",         icon: HeartPulse, title: "Polizza Vita",              landing: "/polizza-vita",               desc: "Temporanea Caso Morte, previdenza (FPA/PIP), investimento Multiramo e soluzioni business." },
  { key: "infortuni",    icon: Activity,   title: "Infortuni e Malattia",      landing: "/infortuni-malattia",         desc: "Invalidità permanente, rimborso spese mediche, indennità da ricovero, patologie gravi." },
  { key: "rc_pro",       icon: Briefcase,  title: "RC Professionale",          landing: "/rc-professionale",           desc: "Copertura errori e omissioni per professionisti e imprese, con tutela legale dedicata." },
  { key: "monopattino",  icon: Zap,        title: "Monopattino Elettrico",     landing: "/assicurazione-monopattino",  desc: "RC obbligatoria per legge, furto del mezzo, tutela legale e assistenza in strada." },
  { key: "viaggi",       icon: Plane,      title: "Assicurazione Viaggi",       landing: null,                          desc: "Annullamento, spese mediche all'estero, bagaglio, assistenza e responsabilità civile in viaggio." },
  { key: "pet",          icon: PawPrint,   title: "Cani e Gatti",              landing: "/assicurazione-cani-gatti",   desc: "Spese veterinarie, responsabilità civile del proprietario, assistenza h24 e tutela legale." },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Soluzioni assicurative — Da Pra Assicurazioni",
  url: "https://dapraassicurazioni.it/soluzioni",
  description: "Panoramica delle soluzioni assicurative Da Pra Assicurazioni: Auto, Moto, Casa, Vita, Infortuni, RC Professionale, Monopattino, Viaggi, Cani e Gatti.",
};

export default function Soluzioni() {
  useSeoMeta({
    title: "Soluzioni Assicurative | Da Pra Assicurazioni Milano",
    description: "Le nostre soluzioni assicurative: Auto, Moto, Casa, Vita, Infortuni, RC Professionale, Monopattino, Viaggi, Cani e Gatti. Agenzia plurimandataria a Milano dal 1958.",
    keywords: "soluzioni assicurative, polizze, assicurazione Milano, agenzia plurimandataria, Da Pra",
    canonical: "https://dapraassicurazioni.it/soluzioni",
    jsonLd: JSON_LD, jsonLdId: "ld-soluzioni",
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <Breadcrumb current={{ label: "Soluzioni", href: "/soluzioni" }} />

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#C5A059" }}>Soluzioni · Dal 1958</div>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl leading-tight text-slate-900">
              Le nostre soluzioni <span className="italic">assicurative.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              Da Pra Assicurazioni è un'<strong>agenzia plurimandataria a Milano dal 1958</strong>: costruiamo insieme
              la protezione più adatta a persone, famiglie e professionisti confrontando le proposte di più compagnie.
            </p>
          </div>
        </div>
      </section>

      {/* Griglia 3×3 */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {SOLUZIONI.map(({ key, icon: Icon, title, landing, desc }) => (
              <article key={key} data-testid={`soluzione-${key}`} className="group border border-slate-200 bg-white p-7 flex flex-col hover:border-slate-900 transition-colors">
                <div className="w-12 h-12 bg-slate-900 text-[#C5A059] flex items-center justify-center">
                  <Icon size={22} />
                </div>
                <h2 className="mt-6 font-serif text-xl md:text-2xl text-slate-900 leading-tight">{title}</h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-grow">{desc}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                  {landing && (
                    <Link to={landing} data-testid={`soluzione-${key}-landing`} className="inline-flex items-center gap-1 text-slate-900 border-b border-slate-900 pb-0.5 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors">
                      Scopri di più
                    </Link>
                  )}
                  <Link to={`/preventivo?type=${key}`} data-testid={`soluzione-${key}-cta`} className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white h-9 px-4 text-xs tracking-wide transition-colors ml-auto">
                    Richiedi preventivo <ChevronRight size={12} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Non trovi quello che cerchi? */}
      <section className="bg-[#FAF9F6] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline" style={{ color: "#C5A059" }}>Consulenza dedicata</div>
            <h2 className="mt-3 font-serif text-2xl md:text-4xl text-slate-900 leading-tight">Non trovi quello che cerchi?</h2>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Raccontaci di cosa hai bisogno. Analizzeremo la richiesta e ti proporremo la soluzione assicurativa più adatta.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/preventivo" data-testid="soluzioni-cta-preventivo" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-sm tracking-wide transition-colors">
              Richiedi un preventivo <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
